import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import React, { useEffect } from 'react'
import PhotoPointNote from 'components/elements/items/PhotoPointNote'
import InformationModal from 'components/elements/modal/InformationModal'
import { ERROR } from 'constants/messages'
import {
  StatesProps,
  useAddressMatch,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import KsSelectDestinationContent from 'components/elements/items/KsSelectDestinationContent'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import TextBoxGroup from 'components/elements/form/TextBoxGroup'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import SelectDamagedImageGroup from 'components/elements/form/SelectDamagedImageGroup'
import {
  DestinationTypes,
  QnStatesProps,
  OthersStatusTypes,
  OthersKsStatusTypes,
  ImageInfo,
  AccidentImageKeys,
  SnowDetailTypes,
  VehicleHeightStatusTypes,
} from 'states/QnState'
import { containsIncompatibleChar, createOptions } from 'utils/commonUtils'
import { isSaficDomain, isSjDomain, isKsDomain, getDomain } from 'utils/domain'
import { success } from 'utils/status'
import KsSelectVehicleModificationContent from 'components/elements/items/KsSelectVehicleModificationContent'
import DOMAIN from 'constants/domains'

const othersStatusOptions = createOptions(
  isKsDomain()
    ? VEHICLE_STATE_FORM.othersKsStatus.options
    : VEHICLE_STATE_FORM.othersStatus.options
)
const snowDetailOptions = createOptions(VEHICLE_STATE_FORM.snowDetail.options)

/**
 * QN-03-h: その他
 */
const Others: React.FC<StatesProps> = (props: QnStatesProps) => {
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const { state, setState } = props
  const {
    othersStatus,
    othersKsStatus,
    destinationType,
    othersDetail,
    othersImage,
    snowDetail,
    vehicleHeightStatus,
  } = editState.vehicleStateInfo

  /** 対応不可のメッセージ表示フラグ */
  const [isShowingMessage, setIsShowingMessage] = React.useState<boolean>(
    ((othersStatus === '' || othersStatus.includes(OthersStatusTypes.snow)) &&
      isSjDomain()) ||
      // 共済系ドメイン時は泥・砂選択時
      (othersKsStatus.includes(OthersKsStatusTypes.sand) && isKsDomain())
  )

  //エラーメッセージ
  const errorMessage = React.useMemo(() => {
    return containsIncompatibleChar(othersDetail)
      ? ERROR.CONTAINS_SURROGATE_PAIR
      : ''
  }, [othersDetail])

  /** 配送先とトラブル場所のバリデーション */
  const addressMatchError = useAddressMatch(editState)

  // バリデーション
  const isDisabled = React.useMemo(() => {
    let isDisable = false
    if (destinationType.includes(DestinationTypes.specify)) {
      if (
        (!othersStatus.includes(OthersStatusTypes.snow) && !isKsDomain()) ||
        // 共済系ドメインかつ、雪もしくは泥・砂以外を選択時
        (!othersKsStatus.includes(OthersKsStatusTypes.snow) &&
          !othersKsStatus.includes(OthersKsStatusTypes.sand) &&
          isKsDomain())
      ) {
        isDisable = !editState.destinationLocation.isSelected
        if (editState.destinationLocation.isSelected) {
          isDisable = addressMatchError
        }
      }
    }
    if (errorMessage) {
      isDisable = true
    }
    return isDisable
  }, [
    othersStatus,
    othersKsStatus,
    destinationType,
    editState.destinationLocation.isSelected,
    errorMessage,
    addressMatchError,
  ])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    if (
      (!isKsDomain() && othersStatus == OthersStatusTypes.snow) ||
      // 共済系ドメイン時は雪もしくは泥・砂が選択時
      ((othersKsStatus.includes(OthersKsStatusTypes.snow) ||
        othersKsStatus.includes(OthersKsStatusTypes.sand)) &&
        isKsDomain())
    ) {
      return ''
    }
    switch (destinationType) {
      case DestinationTypes.specify:
        return isKsDomain() ? WARNING.WRECKER_KS : WARNING.WRECKER
      case DestinationTypes.introduction:
        if (
          // 共済系ドメイン時 かつ「入庫先紹介希望」選択時
          isKsDomain()
        ) {
          return ''
        }
        return WARNING.INTRODUCTION
    }
    return ''
  }, [othersStatus, othersKsStatus, destinationType])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo(() => {
    const transitionDestination = isKsDomain()
      ? PAGE_PATH.CONTRACT_INFORMATION
      : isSaficDomain()
      ? PAGE_PATH.SERVICE_INFORMATION
      : {
          [DOMAIN.SJ]: PAGE_PATH.CONTRACT_INFORMATION,
        }[getDomain()]
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType, getDomain()])

  /** トラブル状態選択変更時のコールバック */
  const handleOthersStatusChanged = React.useCallback(
    (value: OthersStatusTypes) => {
      setVehicleStateInfo({
        othersStatus: value,
      })
      if (value.includes(OthersStatusTypes.snow) && isSjDomain()) {
        setIsShowingMessage(true)
      }
    },
    [setVehicleStateInfo]
  )

  /** トラブル状態選択変更時のコールバック (共済系ドメイン時) */
  const handleOthersKsStatusChanged = React.useCallback(
    (value: OthersKsStatusTypes) => {
      setVehicleStateInfo({
        othersKsStatus: value,
      })
      // 共済系ドメイン時は泥・砂が選択時
      if (value.includes(OthersKsStatusTypes.sand) && isKsDomain()) {
        setIsShowingMessage(true)
      }
    },
    [setVehicleStateInfo]
  )

  /** 雪・泥・砂にはまった場合の詳細選択変更時のコールバック */
  const handleSnowDetailChanged = React.useCallback(
    (value: SnowDetailTypes) => {
      setVehicleStateInfo({
        snowDetail: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** 詳細テキストの入力変更時のコールバック */
  const handleOthersDetailChanged = React.useCallback(
    (value: string) => {
      setVehicleStateInfo({
        othersDetail: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** 画像選択時のコールバック */
  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, imageInfo: ImageInfo) => {
      othersImage[name] = imageInfo
      setVehicleStateInfo({
        othersImage,
      })
    },
    [editState, setVehicleStateInfo]
  )

  // 共済系ドメイン時は雪、泥・砂もしくはその他が選択時に損傷画像コンポーネントを表示
  const showSelectDamagedImageGroup: boolean =
    (!isKsDomain() &&
      (othersStatus.includes(OthersStatusTypes.snow) ||
        othersStatus.includes(OthersStatusTypes.others))) ||
    ((othersKsStatus === OthersKsStatusTypes.snow ||
      othersKsStatus === OthersKsStatusTypes.sand ||
      othersKsStatus === OthersKsStatusTypes.others) &&
      isKsDomain())

  // 共済系ドメイン時は雪、泥・砂以外が選択時に搬送先コンポーネントを表示
  const showSelectVehicleModificationContent: boolean =
    (!(othersStatus == OthersStatusTypes.snow) && !isKsDomain()) ||
    (!(
      othersKsStatus === OthersKsStatusTypes.snow ||
      othersKsStatus === OthersKsStatusTypes.sand
    ) &&
      isKsDomain())

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    setEditingItem({
      ...editState,
      vehicleStateInfo: state.vehicleStateInfo,
      troubleDatetime: state.troubleDatetime,
      vehicleState: state.vehicleState,
      destinationLocation: state.destinationLocation,
    })
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.destinationLocation,
  ])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    isKsDomain()
      ? setVehicleStateInfo({
          othersKsStatus:
            othersKsStatus === '' ? OthersKsStatusTypes.snow : othersKsStatus,
          destinationType:
            destinationType === '' ? DestinationTypes.specify : destinationType,
          vehicleHeightStatus:
            vehicleHeightStatus === ''
              ? VehicleHeightStatusTypes.moreThan9cm
              : vehicleHeightStatus,
        })
      : setVehicleStateInfo({
          othersStatus:
            othersStatus === '' ? OthersStatusTypes.snow : othersStatus,
          destinationType:
            destinationType === '' ? DestinationTypes.specify : destinationType,
          vehicleHeightStatus:
            vehicleHeightStatus === ''
              ? VehicleHeightStatusTypes.moreThan9cm
              : vehicleHeightStatus,
          // SAFICの場合は EditingItemに 初期値 "snowWithNotNormal" または "すでに選択済みのもの" をセットする
          snowDetail:
            isSaficDomain() && snowDetail === ''
              ? SnowDetailTypes.snowWithNotNormal
              : snowDetail,
        })
  }, [])

  return (
    <RequestTemplate
      title={'その他'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          {isKsDomain() ? (
            <RadioButtonGroup
              title={`${VEHICLE_STATE_FORM.othersKsStatus.formName}を選択`}
              isRequired
              options={othersStatusOptions}
              selectedValue={othersKsStatus}
              onValueChanged={handleOthersKsStatusChanged}
            />
          ) : (
            <RadioButtonGroup
              title={`${VEHICLE_STATE_FORM.othersStatus.formName}を選択`}
              isRequired
              options={othersStatusOptions}
              selectedValue={othersStatus}
              onValueChanged={handleOthersStatusChanged}
            />
          )}
          {isSaficDomain() && othersStatus == OthersStatusTypes.snow && (
            <RadioButtonGroup
              title=""
              options={snowDetailOptions}
              selectedValue={snowDetail}
              onValueChanged={handleSnowDetailChanged}
            />
          )}
          <TextBoxGroup
            title={`${VEHICLE_STATE_FORM.othersDetail.formName}を入力（100文字まで入力可能）`}
            text={othersDetail}
            placeholder={VEHICLE_STATE_FORM.othersDetail.label}
            maxLength={{ length: 100 }}
            onChanged={handleOthersDetailChanged}
          />
          {showSelectDamagedImageGroup && (
            <>
              <SelectDamagedImageGroup
                title={`${VEHICLE_STATE_FORM.othersImage.formName}を2枚撮影`}
                accidentImage={othersImage}
                onChanged={handleImageChanged}
              />
              <PhotoPointNote />
            </>
          )}
          {isKsDomain() ? (
            <KsSelectVehicleModificationContent />
          ) : (
            <SelectVehicleModificationContent />
          )}
          {showSelectVehicleModificationContent &&
            (isKsDomain() ? (
              <KsSelectDestinationContent isIntroduction={true} />
            ) : (
              <SelectDestinationContent />
            ))}
          <InformationModal
            on={isShowingMessage}
            content={
              'トラブルの内容が路面状況によるタイヤの空転のみの場合、ロードアシスタンス対象外です。'
            }
            greyBack
            modalFill
            textCenter
            closeLabel="OK"
            closeFunction={(): void => {
              setIsShowingMessage(false)
            }}
          />
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      isDisabled={isDisabled}
      notClearEditingItem
      error={errorMessage}
    />
  )
}

export default Others
